import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Note {
  id?: string;
  content: string;
}

const AddNotePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddNotePage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode 
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Note>({
    content: ''
  });
  
  const [errors, setErrors] = useState({
    content: '',
    server: ''
  });

    const [saveMessage, setSaveMessage] = useState('');

  // Fetch existing note if editing
  useEffect(() => {
    const editId = new URLSearchParams(location.search).get('editId');
    if (editId && quoteId) {
      setEditingId(editId);
      fetchNoteForEdit(editId);
    }
  }, [quoteId, location.search]);

  const fetchNoteForEdit = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/notes/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const note = response.data.data || response.data;
      setFormData({
        content: note.note_content || note.content || ''
      });
    } catch (error) {
      console.error('Error fetching note for edit:', error);
    }
  };

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '', server: '' }));
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = { ...errors };
    
    if (!formData.content.trim()) {
      newErrors.content = 'Note content is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

    const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!validateForm()) return;
  
  // Validate clientId
  if (!clientId || isNaN(parseInt(clientId))) {
    setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
    return;
  }
  
  setIsLoading(true);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');
    
    // Clean the quoteId - convert "null" string or invalid values to actual null
    let cleanQuoteId: number | null = null;
    if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
      cleanQuoteId = parseInt(quoteId);
    }
    
    const noteData = {
      note_content: formData.content,
      quote_id: cleanQuoteId,
      client_id: parseInt(clientId)
    };
    
    // If no quote_id, save to localStorage (draft mode)
    if (!cleanQuoteId) {
      console.log('💾 No quote ID - saving note to localStorage as draft');
      
      const storageKey = `client_${clientId}_notes_draft`;
      const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingDrafts.push({
        ...noteData,
        id: `temp-${Date.now()}`,
        isTemp: true
      });
      localStorage.setItem(storageKey, JSON.stringify(existingDrafts));
      
      setSaveMessage('Note saved to draft! It will be saved when you create the quote.');

      setTimeout(() => {
        if (isMasterQuoteMode && returnTo) {
          // Should not happen for draft mode, but handle gracefully
          const decodedReturnUrl = decodeURIComponent(returnTo);
          navigate(decodedReturnUrl);
        } else {
          navigate(`/create-quote/${clientId}`, { 
            state: { activeTab: 'quotes', refreshProducts: true, hasDrafts: true }
          });
        }
      }, 1500);
    } else {
      // Has quote_id - save to database
      if (editingId) {
        await axios.put(
          `${API_URL}/api/notes/${editingId}`,
          noteData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSaveMessage('Note updated successfully!');
        
        // ✅ LOG ACTIVITY: Note Updated
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const performedBy = user?.username || user?.email || 'System';
          
          await axios.post(
            `${API_URL}/api/quote-activity-logs`,
            {
              quote_id: cleanQuoteId,
              action_type: 'note_updated',
              description: `Note updated: ${formData.content.substring(0, 50) || ''}${formData.content.length > 50 ? '...' : ''}`,
              metadata: {
                productType: 'note',
                productId: editingId,
                performedBy: performedBy,
                contentPreview: formData.content.substring(0, 200) || '',
                contentLength: formData.content.length,
                updatedAt: new Date().toISOString()
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: note_updated');
        } catch (logError) {
          console.error('❌ Failed to log note update:', logError);
          // Don't fail the update if logging fails
        }
      } else {
        await axios.post(
          `${API_URL}/api/notes`,
          noteData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSaveMessage('Note added successfully!');
        
        // ✅ LOG ACTIVITY: Note Added
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const performedBy = user?.username || user?.email || 'System';
          
          await axios.post(
            `${API_URL}/api/quote-activity-logs`,
            {
              quote_id: cleanQuoteId,
              action_type: 'note_added',
              description: `Note added: ${formData.content.substring(0, 50) || ''}${formData.content.length > 50 ? '...' : ''}`,
              metadata: {
                productType: 'note',
                performedBy: performedBy,
                contentPreview: formData.content.substring(0, 200) || '',
                contentLength: formData.content.length,
                addedAt: new Date().toISOString()
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: note_added');
        } catch (logError) {
          console.error('❌ Failed to log note addition:', logError);
          // Don't fail the addition if logging fails
        }
      }
      
      // ✅ FIX: Navigate based on mode
      setTimeout(() => {
        const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
        
        if (!validClientId) {
          console.error('❌ No clientId available for navigation');
          setErrors(prev => ({ ...prev, server: 'Error: Missing client ID' }));
          setIsLoading(false);
          return;
        }
        
        if (isMasterQuoteMode && returnTo) {
          // Master quote mode - navigate back to master quote page
          const decodedReturnUrl = decodeURIComponent(returnTo);
          const separator = decodedReturnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=note&clientId=${validClientId}`;
          console.log('📍 Returning to master quote page:', finalUrl);
          navigate(finalUrl);
        } else if (cleanQuoteId) {
          // Create quote mode but with quoteId - navigate to create quote page
          navigate(`/create-quote/${validClientId}`, { 
            state: { refreshProducts: true, activeTab: 'quotes' } 
          });
        } else {
          // Fallback
          navigate(`/create-quote/${validClientId}`, { 
            state: { refreshProducts: true, activeTab: 'quotes' } 
          });
        }
      }, 1500);
    }
      
  } catch (error: any) {
    setErrors(prev => ({
      ...prev,
      server: error.response?.data?.message || `Failed to ${editingId ? 'update' : 'add'} note`
    }));
  } finally {
    setIsLoading(false);
  }
};

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Note' : 'Create Note'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">

            <button
              onClick={() => {
                if (isMasterQuoteMode && returnTo) {
                  // Go back to master quote page with refresh flag
                  const decodedReturnUrl = decodeURIComponent(returnTo);
                  const separator = decodedReturnUrl.includes('?') ? '&' : '?';
                  const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=note&clientId=${clientId}`;
                  console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
                  navigate(finalUrl);
                } else {
                  // Go back to create quote page
                  navigate(`/create-quote/${clientId}`);
                }
              }}
              className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              ← Back to Quote
            </button>
            
            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                {editingId ? 'Edit Note' : 'Create Note'}
              </h3>
              
              {errors.server && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {errors.server}
                </div>
              )}

              {saveMessage && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                  {saveMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Note Content */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Note Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows={8}
                    className={`w-full px-4 py-3 border ${errors.content ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter your note here..."
                  />
                  {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content}</p>}
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t">

                  <button
                    type="button"
                    onClick={() => {
                      if (isMasterQuoteMode && returnTo) {
                        // Go back to master quote page with refresh flag
                        const decodedReturnUrl = decodeURIComponent(returnTo);
                        const separator = decodedReturnUrl.includes('?') ? '&' : '?';
                        const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=note&clientId=${clientId}`;
                        console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
                        navigate(finalUrl);
                      } else {
                        navigate(`/create-quote/${clientId}`);
                      }
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md disabled:bg-gray-400"
                  >
                    {isLoading ? 'Saving...' : (editingId ? 'Update Note' : 'Add Note')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNotePage;